import { useMemo, useState } from 'react';
import { Link, Navigate } from '@tanstack/react-router';
import { fmtDate, fmtMoney } from '../../lib/format';
import { ApiError } from '../../lib/api';
import { useDashboard, useDashboardPhaseTransition, type DashboardWeekPhase, type DashboardActivity } from './api';
import { toast } from '../../lib/toast';
import { useMe } from '../auth/api';
import OnboardingChecklist from '../onboarding/OnboardingChecklist';
import MorningQueue from './MorningQueue';
import { Skeleton } from '../../components/ui';
import { PROJECT_STATUS_META } from '../projects/types';
import { PHASE_STATUS_META } from '../phases/types';
import PhaseTypeTag from '../phases/PhaseTypeTag';

const DAY = 86_400_000;

function relTime(iso: string): string {
  const diff = Date.now() - +new Date(iso);
  const min = Math.round(diff / 60_000);
  if (min < 1) return 'teď';
  if (min < 60) return `před ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `před ${h} h`;
  const d = Math.round(h / 24);
  if (d < 30) return `před ${d} d`;
  return fmtDate(iso);
}

function startTag(ph: DashboardWeekPhase): { text: string; cls: string } {
  if (ph.overdue) return { text: 'po termínu', cls: 'text-red-600 dark:text-red-400 font-medium' };
  if (ph.status === 'in_progress') return { text: 'probíhá', cls: 'text-amber-600 dark:text-amber-400' };
  if (!ph.plannedStart) return { text: '', cls: '' };
  const days = Math.floor((+new Date(ph.plannedStart) - new Date().setHours(0, 0, 0, 0)) / DAY);
  if (days <= 0) return { text: 'začíná dnes', cls: 'text-zinc-600 dark:text-zinc-300' };
  if (days === 1) return { text: 'zítra', cls: 'text-zinc-500' };
  return { text: `za ${days} d`, cls: 'text-zinc-500' };
}

function WeekPhaseRow({ ph }: { ph: DashboardWeekPhase }) {
  const tag = startTag(ph);
  const trans = useDashboardPhaseTransition();
  const est = ph.estimatedMinutes ?? 0;
  const pct = ph.status === 'done' ? 100 : est > 0 ? Math.min(100, Math.round((ph.actualMinutes / est) * 100)) : 0;
  const act = (to: DashboardWeekPhase['status'], msg: string) =>
    trans.mutate({ id: ph.id, to }, { onSuccess: () => toast.success(msg) });
  return (
    <li className={`flex items-center gap-3 px-4 py-2.5 text-sm ${ph.overdue ? 'bg-red-50/60 dark:bg-red-950/20' : ''}`}>
      <span className={`w-1 h-8 shrink-0 rounded ${PHASE_STATUS_META[ph.status].dot ?? 'bg-zinc-300'}`} />
      <Link to="/vyroby/$propItemId" params={{ propItemId: ph.propItem.id }} className="min-w-0 flex-1">
        <div className="flex items-center gap-2 min-w-0">
          <span className="truncate font-medium hover:underline">{ph.propItem.name}</span>
          <PhaseTypeTag type={ph.type} className="shrink-0 text-xs text-zinc-400" />
        </div>
        <div className="flex items-center gap-2 text-xs text-zinc-500">
          <span className="font-mono">{ph.propItem.project.code}</span>
          {ph.assignedTo && <span>· {ph.assignedTo.fullName}</span>}
          {est > 0 && (
            <span className="flex items-center gap-1">
              · <span className="inline-block h-1.5 w-12 rounded bg-zinc-200 dark:bg-zinc-700 overflow-hidden align-middle"><span className={`block h-full ${pct >= 100 ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{ width: `${pct}%` }} /></span>
              {Math.round((ph.actualMinutes / 60) * 10) / 10}/{Math.round((est / 60) * 10) / 10} h
            </span>
          )}
        </div>
      </Link>
      <div className="flex shrink-0 items-center gap-1">
        {ph.status === 'pending' && (
          <button onClick={() => act('in_progress', 'Fáze spuštěna')} disabled={trans.isPending} title="Spustit" className="rounded border border-zinc-200 dark:border-zinc-700 px-2 py-1 text-xs hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-50">▶</button>
        )}
        {ph.status === 'blocked' && (
          <button onClick={() => act('in_progress', 'Odblokováno')} disabled={trans.isPending} title="Odblokovat" className="rounded border border-zinc-200 dark:border-zinc-700 px-2 py-1 text-xs hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-50">▶</button>
        )}
        {ph.status === 'in_progress' && (
          <button onClick={() => act('done', 'Hotovo ✓')} disabled={trans.isPending} title="Označit hotovo" className="rounded border border-emerald-300 dark:border-emerald-800 px-2 py-1 text-xs text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 disabled:opacity-50">✓</button>
        )}
      </div>
      <div className="w-20 shrink-0 text-right">
        <div className={`text-xs ${tag.cls}`}>{tag.text}</div>
        {ph.plannedEnd && <div className="text-[11px] text-zinc-400">do {fmtDate(ph.plannedEnd)}</div>}
      </div>
    </li>
  );
}


function SectionCard({
  title,
  count,
  empty,
  children,
}: {
  title: string;
  count?: number;
  empty: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 overflow-hidden">
      <div className="border-b border-zinc-100 dark:border-zinc-800 px-4 py-2.5">
        <h2 className="text-[13px] font-semibold tracking-tight">
          {title}
          {count != null && count > 0 && (
            <span className="ml-1.5 text-xs font-normal tabular-nums text-zinc-400">({count})</span>
          )}
        </h2>
      </div>
      {count === 0 ? <p className="px-4 py-6 text-sm text-zinc-400">{empty}</p> : children}
    </section>
  );
}

function deadlineLabel(days: number | null): { text: string; cls: string } {
  if (days == null) return { text: '', cls: 'text-zinc-500' };
  if (days < 0) return { text: 'po termínu', cls: 'text-red-600 dark:text-red-400 font-medium' };
  if (days === 0) return { text: 'dnes', cls: 'text-red-600 dark:text-red-400 font-medium' };
  if (days <= 2) return { text: `za ${days} d`, cls: 'text-red-600 dark:text-red-400' };
  return { text: `za ${days} d`, cls: 'text-amber-600 dark:text-amber-400' };
}

function ActivityRow({ a }: { a: DashboardActivity }) {
  const text = a.body.length > 140 ? a.body.slice(0, 140) + '…' : a.body;
  return (
    <li className="px-4 py-2.5 text-sm">
      <div className="flex items-baseline gap-2">
        <span className="font-medium">{a.author}</span>
        {a.target && (
          a.target.kind === 'project' ? (
            <Link to="/zakazky/$projectId" params={{ projectId: a.target.id }} className="truncate text-xs text-zinc-500 hover:underline">{a.target.label}</Link>
          ) : (
            <Link to="/vyroby/$propItemId" params={{ propItemId: a.target.id }} className="truncate text-xs text-zinc-500 hover:underline">{a.target.label}</Link>
          )
        )}
        <span className="ml-auto shrink-0 text-xs text-zinc-400">{relTime(a.createdAt)}</span>
      </div>
      <p className="mt-0.5 whitespace-pre-line text-zinc-600 dark:text-zinc-300">{text}</p>
    </li>
  );
}

export default function DashboardPage() {
  const { data, isLoading, error } = useDashboard();
  const { data: me } = useMe();
  const canFinance = !!me?.caps.includes('finance');
  const [mineOnly, setMineOnly] = useState(false);

  const firstName = me?.fullName.trim().split(' ')[0] ?? '';
  const hour = new Date().getHours();
  const greeting = hour < 10 ? 'Dobré ráno' : hour < 18 ? 'Dobrý den' : 'Dobrý večer';
  const dateRaw = new Date().toLocaleDateString('cs-CZ', { weekday: 'long', day: 'numeric', month: 'long' });
  const dateLabel = dateRaw.charAt(0).toUpperCase() + dateRaw.slice(1);

  const weekPhases = useMemo(() => {
    const all = data?.weekPhases ?? [];
    return mineOnly && me ? all.filter((ph) => ph.assignedTo?.id === me.id) : all;
  }, [data?.weekPhases, mineOnly, me]);

  // Dílna přistává rovnou na své stanici — dashboard je pro ni bez čísel poloprázdný.
  // (Až za hooky, ať se jejich pořadí mezi rendery nemění.)
  if (me?.role === 'user') return <Navigate to="/stanice" replace />;

  return (
    <div className="max-w-5xl">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-[28px] font-semibold tracking-tight">{greeting}{firstName ? `, ${firstName}` : ''}</h1>
        <p className="mt-1 text-sm text-zinc-500">{dateLabel}{data ? ` · ${data.counts.openProjects} aktivních zakázek` : ''}</p>
      </div>

      <div className="mb-6"><OnboardingChecklist /></div>

      {isLoading && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-20 w-full" />)}
          </div>
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      )}
      {error && (
        <p className="text-sm text-red-600 dark:text-red-400">
          Chyba načtení: {(error as ApiError).message}
        </p>
      )}

      {data && (
        <div className="space-y-6">
          {/* Ranní fronta místo čísel: co dnes vyřešit, s přímou akcí u každé věci. */}
          <MorningQueue items={data.queue ?? []} />

          {/* Tento týden v dílně — rozdělaná práce + co startuje/končí */}
          <section className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 overflow-hidden">
            <div className="flex items-center justify-between border-b border-zinc-200 dark:border-zinc-800 px-4 py-2.5">
              <h2 className="text-sm font-semibold">
                Tento týden v dílně
                {weekPhases.length > 0 && <span className="ml-1.5 text-xs font-normal text-zinc-500">({weekPhases.length})</span>}
              </h2>
              <div className="flex overflow-hidden rounded-md border border-zinc-200 dark:border-zinc-700 text-xs">
                <button onClick={() => setMineOnly(false)} className={`px-2.5 py-1 ${!mineOnly ? 'bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900' : 'text-zinc-500'}`}>Vše</button>
                <button onClick={() => setMineOnly(true)} className={`px-2.5 py-1 ${mineOnly ? 'bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900' : 'text-zinc-500'}`}>Jen moje</button>
              </div>
            </div>
            {weekPhases.length === 0 ? (
              <p className="px-4 py-6 text-sm text-zinc-400">{mineOnly ? 'Nemáš přiřazené žádné fáze na tento týden.' : 'Tento týden není naplánovaná žádná práce.'}</p>
            ) : (
              <ul className="divide-y divide-zinc-100 dark:divide-zinc-800">
                {weekPhases.map((ph) => <WeekPhaseRow key={ph.id} ph={ph} />)}
              </ul>
            )}
          </section>

          {/* Finanční souhrn — nabídka vs. realita (jen s oprávněním finance) */}
          {canFinance && data.finance && (
          <section className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 overflow-hidden">
            <div className="border-b border-zinc-200 dark:border-zinc-800 px-4 py-2.5">
              <h2 className="text-sm font-semibold">Finance — aktivní zakázky (objednáno vs. realita)</h2>
            </div>
            <div className="grid grid-cols-3 divide-x divide-zinc-100 dark:divide-zinc-800 border-b border-zinc-200 dark:border-zinc-800">
              <div className="p-4">
                <div className="text-xs text-zinc-500" title="Součet přijatých nabídek bez DPH">Objednáno celkem</div>
                <div className="text-xl font-bold tabular-nums">{fmtMoney(data.finance.quotedTotal)}</div>
              </div>
              <div className="p-4">
                <div className="text-xs text-zinc-500">Skutečný náklad</div>
                <div className="text-xl font-bold tabular-nums">{fmtMoney(data.finance.actualCostTotal)}</div>
              </div>
              <div className="p-4">
                <div className="text-xs text-zinc-500">Hrubá marže (rozdíl)</div>
                <div className={`text-xl font-bold tabular-nums ${data.finance.profitTotal >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                  {fmtMoney(data.finance.profitTotal)}
                </div>
              </div>
            </div>
            {data.finance.projects.length === 0 ? (
              <p className="px-4 py-6 text-sm text-zinc-400">Žádné aktivní zakázky.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wide text-zinc-500">
                  <tr>
                    <th className="px-4 py-2 font-medium">Zakázka</th>
                    <th className="px-4 py-2 font-medium text-right">Nabídka</th>
                    <th className="px-4 py-2 font-medium text-right">Náklad</th>
                    <th className="px-4 py-2 font-medium text-right">Marže</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
                  {data.finance.projects.map((p) => (
                    <tr key={p.id} className="hover:bg-zinc-50 dark:hover:bg-zinc-900/40">
                      <td className="px-4 py-2">
                        <Link to="/zakazky/$projectId" params={{ projectId: p.id }} className="hover:underline">
                          <span className="font-mono text-xs text-zinc-500">{p.code}</span> {p.name}
                        </Link>
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums">{p.quoted != null ? fmtMoney(p.quoted, p.currency) : '—'}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-zinc-500">{fmtMoney(p.actualCost, p.currency)}</td>
                      <td className={`px-4 py-2 text-right tabular-nums ${p.profit == null ? 'text-zinc-400' : p.profit >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                        {p.profit != null ? fmtMoney(p.profit, p.currency) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
          )}

          {canFinance && (
          <SectionCard
            title="Faktury po splatnosti"
            count={data.overdueInvoices.length}
            empty="Žádné faktury po splatnosti. 👍"
          >
            <ul className="divide-y divide-zinc-100 dark:divide-zinc-800">
              {data.overdueInvoices.map((inv) => (
                <li key={inv.id} className="flex items-center gap-3 px-4 py-2.5 text-sm bg-red-50/40 dark:bg-red-950/10">
                  <span className="font-mono text-xs text-zinc-500 w-28 shrink-0 truncate">{inv.number}</span>
                  <Link to={inv.link} className="min-w-0 flex-1 truncate hover:underline">{inv.label}</Link>
                  <span className="shrink-0 tabular-nums font-medium">{fmtMoney(inv.totalPrice, inv.currency)}</span>
                  <span className="shrink-0 w-24 text-right text-xs text-red-600 dark:text-red-400 font-medium">
                    {inv.daysOverdue} {inv.daysOverdue === 1 ? 'den' : inv.daysOverdue < 5 ? 'dny' : 'dnů'} po spl.
                  </span>
                </li>
              ))}
            </ul>
          </SectionCard>
          )}

          <SectionCard
            title={`Blížící se deadliny (≤ ${data.warningDays} dní)`}
            count={data.upcomingDeadlines.length}
            empty="Žádné blížící se termíny. 🎉"
          >
            <ul className="divide-y divide-zinc-100 dark:divide-zinc-800">
              {data.upcomingDeadlines.map((p) => {
                const dl = deadlineLabel(p.daysUntil);
                return (
                  <li key={p.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                    <Link
                      to="/zakazky/$projectId"
                      params={{ projectId: p.id }}
                      className="font-mono text-xs text-zinc-500 hover:underline w-28 shrink-0"
                    >
                      {p.code}
                    </Link>
                    <Link
                      to="/zakazky/$projectId"
                      params={{ projectId: p.id }}
                      className="min-w-0 flex-1 truncate hover:underline"
                    >
                      {p.name}
                    </Link>
                    <span
                      className={`shrink-0 rounded px-1.5 py-0.5 text-xs ${PROJECT_STATUS_META[p.status].badge}`}
                    >
                      {PROJECT_STATUS_META[p.status].label}
                    </span>
                    <span className="shrink-0 w-28 text-right text-zinc-500">{fmtDate(p.deadline)}</span>
                    <span className={`shrink-0 w-20 text-right text-xs ${dl.cls}`}>{dl.text}</span>
                  </li>
                );
              })}
            </ul>
          </SectionCard>

          <div className="grid md:grid-cols-2 gap-6">
            <SectionCard
              title="Blokované fáze"
              count={data.blockedPhases.length}
              empty="Nic není blokované."
            >
              <ul className="divide-y divide-zinc-100 dark:divide-zinc-800">
                {data.blockedPhases.map((ph) => (
                  <li key={ph.id} className="px-4 py-2.5 text-sm">
                    <div className="flex items-center gap-2">
                      <Link
                        to="/vyroby/$propItemId"
                        params={{ propItemId: ph.propItem.id }}
                        className="font-medium hover:underline"
                      >
                        {ph.propItem.name}
                      </Link>
                      <PhaseTypeTag type={ph.type} className="text-xs text-zinc-400" />
                      <span className="ml-auto font-mono text-xs text-zinc-400">
                        {ph.propItem.project.code}
                      </span>
                    </div>
                    {ph.blockReason && (
                      <p className="mt-0.5 text-xs text-red-600 dark:text-red-400">{ph.blockReason}</p>
                    )}
                  </li>
                ))}
              </ul>
            </SectionCard>

            <SectionCard
              title="Materiál pod minimem"
              count={data.lowStock.length}
              empty="Vše naskladněno."
            >
              <ul className="divide-y divide-zinc-100 dark:divide-zinc-800">
                {data.lowStock.map((m) => (
                  <li key={m.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                    <span className="font-mono text-xs text-zinc-500 w-24 shrink-0 truncate">{m.sku}</span>
                    <Link to="/sklad" className="min-w-0 flex-1 truncate hover:underline">
                      {m.name}
                    </Link>
                    <span className="shrink-0 text-xs text-red-600 dark:text-red-400 font-medium tabular-nums">
                      {Number(m.stockQty)} / {Number(m.minQty)} {m.unit}
                    </span>
                  </li>
                ))}
              </ul>
            </SectionCard>
          </div>

          <SectionCard title="Aktivita ze stavebního deníku" count={data.activity.length} empty="Zatím žádné zápisy.">
            <ul className="divide-y divide-zinc-100 dark:divide-zinc-800">
              {data.activity.map((a) => (
                <ActivityRow key={a.id} a={a} />
              ))}
            </ul>
          </SectionCard>
        </div>
      )}
    </div>
  );
}
